import React, { useMemo } from 'react';

export default function LoginScreen({ onLogin, error, isLoading }) {
  // Generate random stars with varied sizes and timings
  const stars = useMemo(
    () =>
      Array.from({ length: 120 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        size: `${1 + Math.random() * 2.5}px`,
        delay: `${Math.random() * 5}s`,
        duration: `${3 + Math.random() * 4}s`,
        opacity: 0.15 + Math.random() * 0.6,
      })),
    []
  );

  // Floating orbs for extra depth
  const orbs = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => ({
        id: i,
        left: `${15 + Math.random() * 70}%`,
        top: `${15 + Math.random() * 70}%`,
        size: `${80 + Math.random() * 200}px`,
        delay: `${i * 2}s`,
        duration: `${12 + Math.random() * 10}s`,
        color: i % 2 === 0 ? 'rgba(0,240,255,0.04)' : 'rgba(168,85,247,0.04)',
      })),
    []
  );

  return (
    <>
      <div className="stars">
        {stars.map((s) => (
          <div
            key={s.id}
            className="star"
            style={{
              left: s.left,
              top: s.top,
              width: s.size,
              height: s.size,
              animationDelay: s.delay,
              animationDuration: s.duration,
              opacity: s.opacity,
            }}
          />
        ))}
      </div>

      <div className="floating-orbs">
        {orbs.map((o) => (
          <div
            key={o.id}
            className="floating-orb"
            style={{
              left: o.left,
              top: o.top,
              width: o.size,
              height: o.size,
              animationDelay: o.delay,
              animationDuration: o.duration,
              background: `radial-gradient(circle, ${o.color}, transparent 70%)`,
            }}
          />
        ))}
      </div>

      <div className="bg-mesh" />

      <div className="login-screen">
        <div className="login-logo-wrapper">
          <div className="login-logo">InboxPulse</div>
          <div className="login-logo-glow">InboxPulse</div>
        </div>

        <p className="login-subtitle">AI-Powered Email Intelligence</p>

        <p className="login-tagline">
          Connect your Gmail to automatically sort, prioritize, and summarize 
          your inbox. Know what matters in seconds.
        </p>

        <div className="login-pricing glass anim-rise">
          <div className="login-price-pill">Free tier · 15 AI analyses / month</div>
          <div className="login-price-row">
            <span><strong>Pro</strong> · 500 / mo · mock $9</span>
            <span className="login-price-dot">·</span>
            <span><strong>Elite 24/7</strong> · 5k / mo · mock $36</span>
          </div>
          <p className="login-price-note">Paid tiers are simulated in this demo — wire Stripe for real billing.</p>
        </div>

        <button
          className="btn btn-primary login-btn"
          onClick={onLogin}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="btn-spinner" />
              Connecting...
            </>
          ) : (
            <>⚡ Connect with Gmail</>
          )}
        </button>

        {error && (
          <div className="login-error">
            <span>⚠️</span> {error}
          </div>
        )}

        <div className="login-features">
          <div className="login-feature">
            <span className="feature-icon">🔍</span>
            <span>Smart Sorting</span>
          </div>
          <div className="login-feature">
            <span className="feature-icon">⚡</span>
            <span>AI Summaries</span>
          </div>
          <div className="login-feature">
            <span className="feature-icon">🎯</span>
            <span>Priority Tiers</span>
          </div>
        </div>
      </div>
    </>
  );
}
